import React from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded'
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded'
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded'
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import EmployeeSelector from '../../../../events/EmployeeSelector'
import BaseLocationPicker from '../../../../../components/BaseLocationPicker'
import WorkIcon from '@mui/icons-material/Work'
import CalendarIcon from '@mui/icons-material/CalendarMonth'
import DateRangeSelector from '../../../../../components/DateTime/DateRangeSelector'

// Phones stack the fields, so each one fills the row; the per-field
// minWidth/flex hints only apply once the row layout kicks in (lg+).
const FilterField = ({ icon, label, children, minWidth = 220, ...props }) => (
  <Stack
    spacing={0.75}
    sx={{
      flex: 1,
      width: { xs: '100%', lg: 'auto' },
      minWidth: { xs: 0, lg: minWidth },
      ...props,
    }}
  >
    <Stack direction="row" spacing={0.75} alignItems="center">
      {icon}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 900,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {label}
      </Typography>
    </Stack>
    {children}
  </Stack>
)

const Filters = ({ filter, setFilter }) => {
  const hasFilters = !!filter?.employee || !!filter?.baseLocations?.length

  const resetFilters = () => {
    setFilter((prev) => ({
      ...prev,
      shiftType: 'All',
      employee: null,
      baseLocations: [],
    }))
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 4,
        bgcolor: 'background.paper',
        boxShadow: '0 10px 28px rgba(15,23,42,0.06)',
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          gap={1}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2.5,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'primary.main',
                color: '#fff',
              }}
            >
              <FilterAltRoundedIcon />
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={950}>
                Filter overtime claims
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Refine claims by shift type, employee, or base location.
              </Typography>
            </Box>
          </Stack>

          <Button
            size="small"
            variant="outlined"
            startIcon={<RestartAltRoundedIcon />}
            onClick={resetFilters}
            disabled={!hasFilters}
            sx={{
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 800,
            }}
          >
            Reset
          </Button>
        </Stack>

        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          alignItems="flex-start"
        >
          <FilterField
            label="Date Range"
            icon={<CalendarIcon fontSize="small" color="action" />}
            flex={0.4}
            minWidth={160}
          >
            <DateRangeSelector
              value={{ startDate: filter.startDate, endDate: filter.endDate }}
              onChange={(value) => {
                setFilter((prev) => ({
                  ...prev,
                  startDate: value.startDate,
                  endDate: value.endDate,
                }))
              }}
            />
          </FilterField>

          <FilterField
            label="Shift Type"
            icon={<WorkIcon fontSize="small" color="action" />}
            flex={0.4}
            minWidth={140}
          >
            <Autocomplete
              options={['All', 'PTS', 'Event', 'Frontline', 'Admin']}
              value={filter.shiftType ?? 'All'}
              disableClearable
              onChange={(e, newValue) =>
                setFilter((prev) => ({ ...prev, shiftType: newValue }))
              }
              renderInput={(params) => <TextField {...params} />}
            />
          </FilterField>

          <FilterField
            label="Employee"
            icon={<PersonSearchRoundedIcon fontSize="small" color="action" />}
            flex={1}
            minWidth={340}
          >
            <EmployeeSelector
              value={filter?.employee}
              onChange={(e, newValue) =>
                setFilter((prev) => ({ ...prev, employee: newValue }))
              }
              label=""
              fullWidth
              shrinkLabel
            />
          </FilterField>

          {/* TODO: BASE LOCATIONS NOT CURRENTLY PART OF THE SHIFT SCHEMA. EITHER CREATE A VIRTUAL OR REFERENCE THE PTS DUTY / EVENT */}
          {/* <FilterField
            label="Base Locations"
            icon={<BusinessRoundedIcon fontSize="small" color="action" />}
          >
            <BaseLocationPicker
              selected={filter?.baseLocations}
              multiple
              onChange={(baseLocations) =>
                setFilter((prev) => ({
                  ...prev,
                  baseLocations,
                }))
              }
            />
          </FilterField> */}
        </Stack>
      </Stack>
    </Paper>
  )
}

export default Filters
